import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f6f9fc 0%, #ecf0f3 100%);
  padding: 20px;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 6px solid rgba(0, 191, 166, 0.2);
  border-radius: 50%;
  border-top-color: #00bfa6;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 1.4rem;
  color: #333;
  font-weight: 600;
  text-align: center;
  margin-top: 20px;
`;

const LoadingSubText = styled.div`
  font-size: 1rem;
  color: #666;
  margin-top: 10px;
  text-align: center;
  max-width: 80%;
`;

const ErrorContainer = styled.div`
  margin-top: 15px;
  padding: 10px 15px;
  background-color: rgba(255, 0, 0, 0.1);
  border-radius: 8px;
  color: #d32f2f;
  max-width: 80%;
  text-align: center;
`;

const Button = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #00bfa6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #00a090;
  }
`;

const StatusContainer = styled.div`
  margin-top: 20px;
  padding: 10px;
  background-color: rgba(0, 191, 166, 0.1);
  border-radius: 8px;
  width: 80%;
  max-width: 400px;
`;

const StatusItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 5px 0;
  font-size: 0.9rem;
  
  .status-label {
    font-weight: 500;
    color: #555;
  }
  
  .status-value {
    font-weight: 600;
    color: #00bfa6;
    
    &.warning {
      color: #f57c00;
    }
    
    &.error {
      color: #d32f2f;
    }
    
    &.success {
      color: #00bfa6;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: rgba(0, 0, 0, 0.1);
  width: 100%;
  margin: 10px 0;
`;

const MapLink = styled.a`
  display: inline-block;
  margin-top: 15px;
  padding: 8px 15px;
  background-color: #2196f3;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #1976d2;
  }
  
  &:active {
    background-color: #0d47a1;
  }
`;

const CoordinatesDisplay = styled.div`
  margin-top: 10px;
  font-size: 0.85rem;
  color: #555;
  direction: ltr;
  text-align: center;
`;

interface LoadingScreenProps {
  message?: string;
  subMessage?: string;
  error?: string;
  onRetry?: () => void;
  locationStatus?: {
    isLocationSupported?: boolean;
    isLocationPermitted?: boolean;
    accuracy?: number;
    usingDefault?: boolean;
    mapsLink?: string;
    latitude?: number;
    longitude?: number;
  };
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
  message = "جاري تحميل التطبيق...", 
  subMessage = "نحن نحدد موقعك لعرض البيانات المناسبة لمنطقتك",
  error,
  onRetry,
  locationStatus = {}
}) => {
  const isError = !!error;
  const [dots, setDots] = useState('.');
  
  // إضافة نقاط متحركة لشاشة التحميل
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '.' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  // تقييم حالة الموقع
  const getAccuracyStatus = (accuracy?: number) => {
    if (!accuracy) return { text: 'غير معروفة', className: 'warning' };
    if (accuracy < 100) return { text: 'جيدة جدًا', className: 'success' };
    if (accuracy < 500) return { text: 'جيدة', className: 'success' };
    if (accuracy < 1000) return { text: 'متوسطة', className: 'warning' };
    return { text: 'منخفضة', className: 'error' };
  };
  
  const accuracyStatus = getAccuracyStatus(locationStatus.accuracy);
  
  return (
    <LoadingContainer>
      <LoadingSpinner />
      <LoadingText>{message}{dots}</LoadingText>
      {subMessage && <LoadingSubText>{subMessage}</LoadingSubText>}
      
      {/* عرض حالة تحديد الموقع إذا كانت متوفرة */}
      {!isError && (
        <StatusContainer>
          <StatusItem>
            <span className="status-label">دعم تحديد الموقع:</span>
            <span className={`status-value ${locationStatus.isLocationSupported === false ? 'error' : 'success'}`}>
              {locationStatus.isLocationSupported === false ? 'غير مدعوم' : 'مدعوم'}
            </span>
          </StatusItem>
          
          <StatusItem>
            <span className="status-label">إذن تحديد الموقع:</span>
            <span className={`status-value ${locationStatus.isLocationPermitted === false ? 'error' : 'success'}`}>
              {locationStatus.isLocationPermitted === false ? 'غير ممنوح' : 'ممنوح'}
            </span>
          </StatusItem>
          
          {locationStatus.accuracy && (
            <StatusItem>
              <span className="status-label">دقة الموقع:</span>
              <span className={`status-value ${accuracyStatus.className}`}>
                {accuracyStatus.text} ({locationStatus.accuracy.toFixed(0)} متر)
              </span>
            </StatusItem>
          )}
          
          <Divider />
          
          <StatusItem>
            <span className="status-label">نوع البيانات:</span>
            <span className={`status-value ${locationStatus.usingDefault ? 'warning' : 'success'}`}>
              {locationStatus.usingDefault ? 'البيانات العامة' : 'بيانات المنطقة المحددة'}
            </span>
          </StatusItem>
          
          {/* إضافة عرض الإحداثيات ورابط الخريطة */}
          {locationStatus.latitude && locationStatus.longitude && (
            <>
              <Divider />
              <CoordinatesDisplay>
                Lat: {locationStatus.latitude.toFixed(6)}, Long: {locationStatus.longitude.toFixed(6)}
              </CoordinatesDisplay>
              {locationStatus.mapsLink && (
                <MapLink href={locationStatus.mapsLink} target="_blank" rel="noopener noreferrer">
                  عرض موقعك على خريطة جوجل
                </MapLink>
              )}
            </>
          )}
        </StatusContainer>
      )}
      
      {isError && (
        <>
          <ErrorContainer>{error}</ErrorContainer>
          {onRetry && (
            <Button onClick={onRetry}>إعادة المحاولة</Button>
          )}
        </>
      )}
    </LoadingContainer>
  );
};

export default LoadingScreen; 